// @givmo/social — the module's pages, one per view (SMM-WEB1). The host mounts this beneath its
// `ModuleGate` (which decides absent / off / setting up / locked first), on the route that names the
// view, and hands it the organization and the person's permissions. No page branches on what kind
// of organization it serves.

import { useState } from 'react';
import { type SocialPostOut } from '@givmo/api-client';
import { Button, Card, ErrorMessage, testIdProp } from '@givmo/design-system';
import { AccountsPage } from './accounts-page';
import { ComposerPage, NO_ENTRY, readComposerEntry, type ComposerEntry } from './composer-page';
import { ConnectReturnPage } from './connect-return-page';
import {
  EditScheduledDialog,
  VariantRow,
  actionErrorWords,
  editableWhenScheduled,
  postWhen,
  useCancelPost,
} from './post-parts';
import { PostsPage, readContextFilter } from './posts-page';
import { canPublish, canRead, type SocialConfig } from './social-config';
import { usePost } from './social-queries';
import { readRescheduleTime } from './social-reschedule';
import { SOCIAL_PAGE_TEST_ID, SOCIAL_ROUTES, type SocialView } from './social-routes';
import { SocialLoading, SocialPageShell, SocialRefused, SocialStateCard } from './social-ui';
import { socialWords as w } from './social-words';

function currentSearch(): string {
  return globalThis.location?.search ?? '';
}

export function SocialPage({
  config,
  view = { page: 'posts' },
  entry,
}: {
  config: SocialConfig;
  view?: SocialView;
  /** The Email Campaign entry, by embed; a deep link's is read from the address. */
  entry?: ComposerEntry;
}) {
  const [search] = useState(currentSearch);
  if (!canRead(config)) {
    return (
      <SocialPageShell title={w.moduleName} testId="social-locked">
        <SocialStateCard
          testId="social-no-access"
          title={w.noAccessTitle}
          description={w.noAccessBody}
        />
      </SocialPageShell>
    );
  }
  switch (view.page) {
    case 'posts':
      return <PostsPage config={config} context={readContextFilter(search)} />;
    case 'compose':
      return canPublish(config) ? (
        <ComposerPage config={config} entry={entry ?? readComposerEntry(search)} />
      ) : (
        <PostsPage config={config} />
      );
    case 'post':
      return <PostView config={config} postId={view.postId} />;
    case 'accounts':
      return <AccountsPage config={config} />;
    case 'connected':
      return <ConnectReturnPage config={config} />;
  }
}

function PostView({ config, postId }: { config: SocialConfig; postId: string }) {
  const post = usePost(config.orgRef, postId);
  if (config.orgRef === null || post.isPending) {
    return (
      <SocialPageShell
        title={w.postTitle}
        testId={SOCIAL_PAGE_TEST_ID}
        back={{ to: SOCIAL_ROUTES.posts, label: w.backToPosts }}
      >
        <SocialLoading>{w.loadingPost}</SocialLoading>
      </SocialPageShell>
    );
  }
  if (post.isError) {
    return (
      <SocialPageShell
        title={w.postTitle}
        testId={SOCIAL_PAGE_TEST_ID}
        back={{ to: SOCIAL_ROUTES.posts, label: w.backToPosts }}
      >
        <SocialRefused
          error={post.error}
          onRetry={() => void post.refetch()}
          gone={{ title: w.postGoneTitle, description: w.postGoneBody }}
        />
      </SocialPageShell>
    );
  }
  if (post.data.editable && canPublish(config)) {
    // A scheduled post opened for editing starts its Schedule from the time it had.
    return (
      <ComposerPage
        key={post.data.post_id}
        config={config}
        post={post.data}
        entry={{ ...NO_ENTRY, suggestedAt: readRescheduleTime(post.data.post_id) }}
      />
    );
  }
  return <PostDetail orgRef={config.orgRef} post={post.data} canAct={canPublish(config)} />;
}

/** A post that has gone (or is going) out, or a draft a reader cannot edit: read-only, with each
 *  account's state and the acts on it. */
function PostDetail({
  orgRef,
  post,
  canAct,
}: {
  orgRef: string;
  post: SocialPostOut;
  canAct: boolean;
}) {
  const cancel = useCancelPost(orgRef, post);
  const canCancel = canAct && post.variants.some((variant) => variant.can_cancel);
  const canEditScheduled = canAct && editableWhenScheduled(post);
  const [editingScheduled, setEditingScheduled] = useState(false);
  return (
    <SocialPageShell
      title={w.postTitle}
      testId={SOCIAL_PAGE_TEST_ID}
      width="narrow"
      back={{ to: SOCIAL_ROUTES.posts, label: w.backToPosts }}
      actions={
        canCancel ? (
          <div className="flex items-center gap-8">
            <Button
              appearance="saas"
              tone="ghost"
              loading={cancel.isPending}
              onClick={() => cancel.mutate()}
              testId="social-post-cancel"
            >
              {w.cancelScheduled}
            </Button>
            {canEditScheduled ? (
              <Button
                appearance="saas"
                tone="secondary"
                onClick={() => setEditingScheduled(true)}
                testId="social-post-edit-scheduled"
              >
                {w.editScheduled}
              </Button>
            ) : null}
          </div>
        ) : undefined
      }
    >
      <Card className="flex flex-col gap-14 px-24 py-20" testId="social-post-detail">
        <p className="whitespace-pre-line break-words font-sans text-s14 tracking-givmo text-text-strong">
          {post.text}
        </p>
        {post.link_url ? (
          <a
            href={post.link_url}
            target="_blank"
            rel="noreferrer"
            className="w-fit break-all font-sans text-s13 text-brand-600 underline-offset-2 hover:underline"
          >
            {post.link_url}
          </a>
        ) : null}
        {post.image ? (
          <img
            src={post.image.url}
            alt=""
            className="max-h-[320px] w-fit max-w-full rounded-md border border-border-subtle"
          />
        ) : null}
        <p
          className="font-sans text-s12 tracking-givmo text-text-secondary"
          {...testIdProp('social-post-when')}
        >
          {postWhen(post)}
          {post.author_name ? ` · ${w.by(post.author_name)}` : ''}
        </p>
        {cancel.isError ? (
          <ErrorMessage testId="social-post-cancel-error">
            {actionErrorWords(cancel.error)}
          </ErrorMessage>
        ) : null}
        <ul className="flex flex-col border-t border-border-subtle pt-4">
          {post.variants.map((variant) => (
            <VariantRow
              key={variant.variant_id}
              orgRef={orgRef}
              variant={variant}
              canAct={canAct}
            />
          ))}
        </ul>
      </Card>
      {editingScheduled ? (
        <EditScheduledDialog
          orgRef={orgRef}
          post={post}
          onClose={() => setEditingScheduled(false)}
        />
      ) : null}
    </SocialPageShell>
  );
}
